import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useLanguage } from '../contexts/LanguageContext';
import { vehicleAPI, reservationAPI } from '../utils/api';
import { toast } from 'react-toastify';
import './Reservation.css';

const Reservation = () => {
    const { language, t } = useLanguage();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        vehicle_id: searchParams.get('vehicle') || '',
        rental_type: 'hourly', // 'hourly' veya 'daily'
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        reservation_date: null,
        start_hour: '',
        duration_hours: 1,
        pickup_location: '',
        dropoff_location: ''
    });

    const [availableHours, setAvailableHours] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    useEffect(() => {
        fetchVehicles();
    }, []);

    useEffect(() => {
        if (formData.vehicle_id) {
            const vehicle = vehicles.find(v => v.id === parseInt(formData.vehicle_id));
            setSelectedVehicle(vehicle);
        }
    }, [formData.vehicle_id, vehicles]);

    useEffect(() => {
        if (formData.vehicle_id && formData.reservation_date && formData.rental_type === 'hourly') {
            checkAvailability();
        }
    }, [formData.vehicle_id, formData.reservation_date, formData.rental_type]);

    const fetchVehicles = async () => {
        try {
            setLoading(true);
            const response = await vehicleAPI.getAll();
            setVehicles(response.data.data);
        } catch (error) {
            toast.error('Araçlar yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const checkAvailability = async () => {
        try {
            const dateStr = formData.reservation_date.toISOString().split('T')[0];
            const response = await vehicleAPI.checkAvailability(formData.vehicle_id, dateStr);
            setAvailableHours(response.data.data.availableHours);
        } catch (error) {
            console.error('Availability check error:', error);
            setAvailableHours([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            reservation_date: date,
            start_hour: '' // Reset hour when date changes
        }));
    };

    const getMinDate = () => {
        const now = new Date();
        now.setHours(now.getHours() + 3); // 3 saat sonrası
        return now;
    };

    const getMinHour = () => {
        const now = new Date();
        const selectedDate = formData.reservation_date;

        if (!selectedDate) return 0;

        // Seçilen tarih bugünse, şu andan 3 saat sonrasını minimum yap
        if (selectedDate.toDateString() === now.toDateString()) {
            return now.getHours() + 3;
        }

        return 0;
    };

    const getAvailableStartHours = () => {
        const minHour = getMinHour();
        return availableHours.filter(hour => hour >= minHour);
    };

    const getAvailableDurations = () => {
        if (!formData.start_hour) return [];

        const startHour = parseInt(formData.start_hour);
        const durations = [];

        // Arka arkaya kaç saat müsait kontrolü
        for (let i = 1; i <= 12; i++) {
            let allAvailable = true;
            for (let j = 0; j < i; j++) {
                if (!availableHours.includes(startHour + j)) {
                    allAvailable = false;
                    break;
                }
            }
            if (allAvailable) {
                durations.push(i);
            } else {
                break;
            }
        }

        return durations;
    };

    const validateForm = () => {
        if (!formData.vehicle_id) {
            toast.error(t('selectVehicle'));
            return false;
        }
        if (!formData.customer_name.trim()) {
            toast.error(t('required') + ': ' + t('fullName'));
            return false;
        }
        if (!formData.customer_phone.trim()) {
            toast.error(t('required') + ': ' + t('phone'));
            return false;
        }
        if (!formData.customer_email.trim() || !formData.customer_email.includes('@')) {
            toast.error(t('invalidEmail'));
            return false;
        }
        if (!formData.reservation_date) {
            toast.error(t('selectDate'));
            return false;
        }
        // Sadece saatlik kiralamada saat seçimi zorunlu
        if (formData.rental_type === 'hourly' && !formData.start_hour) {
            toast.error(t('selectTime'));
            return false;
        }
        if (!formData.duration_hours) {
            toast.error(t('selectDuration'));
            return false;
        }
        if (!formData.pickup_location.trim()) {
            toast.error(t('required') + ': ' + t('pickupLocation'));
            return false;
        }
        if (!formData.dropoff_location.trim()) {
            toast.error(t('required') + ': ' + t('dropoffLocation'));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setSubmitting(true);

            const reservationData = {
                ...formData,
                reservation_date: formData.reservation_date.toISOString().split('T')[0],
                // Günlük kiralamada saat seçimi yok, default olarak 9:00 kullan
                start_hour: formData.rental_type === 'daily' ? 9 : parseInt(formData.start_hour),
                duration_hours: parseInt(formData.duration_hours),
                rental_type: formData.rental_type, // rental_type'ı ekliyoruz
                language
            };

            const response = await reservationAPI.create(reservationData);

            toast.success(t('reservationSuccess'));
            toast.info(t('confirmationEmail'));

            // Form'u temizle
            setFormData({
                vehicle_id: '',
                rental_type: 'hourly',
                customer_name: '',
                customer_phone: '',
                customer_email: '',
                reservation_date: null,
                start_hour: '',
                duration_hours: 1,
                pickup_location: '',
                dropoff_location: ''
            });

            // 2 saniye sonra anasayfaya yönlendir
            setTimeout(() => {
                navigate('/');
            }, 2000);

        } catch (error) {
            console.error('Reservation error:', error);
            const message = error.response?.data?.message || t('reservationError');
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const getVehicleName = (vehicle) => {
        if (!vehicle) return '';
        return vehicle[`name_${language}`] || vehicle.name_tr;
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;

        // Eğer imagePath zaten tam URL ise, direkt kullan
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }

        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://elittaksibodrum.com';

        // Eğer path /api ile başlamıyorsa, /api ekle
        if (!imagePath.startsWith('/api/')) {
            // /uploads/vehicles/... → /api/uploads/vehicles/...
            if (imagePath.startsWith('/uploads/')) {
                return `${baseUrl}/api${imagePath}`;
            }
            // Diğer durumlar için /api ekle
            return `${baseUrl}/api${imagePath}`;
        }

        // Zaten /api ile başlıyorsa, direkt ekle
        return `${baseUrl}${imagePath}`;
    };

    const calculateTotalPrice = () => {
        if (!selectedVehicle || !formData.duration_hours) return 0;

        if (formData.rental_type === 'hourly') {
            return selectedVehicle.hourly_price * formData.duration_hours;
        } else {
            // Günlük fiyat yoksa saatlik * 24 * 0.8 (indirimli)
            const dailyPrice = selectedVehicle.daily_price || (selectedVehicle.hourly_price * 24 * 0.8);
            return dailyPrice * formData.duration_hours;
        }
    };

    const getDurationLabel = () => {
        if (formData.rental_type === 'hourly') {
            return 'Kiralama Süresi (Saat)';
        }
        return 'Kiralama Süresi (Gün)';
    };

    const getDurationUnit = (count) => {
        if (formData.rental_type === 'hourly') {
            return count === 1 ? 'saat' : 'saat';
        }
        return count === 1 ? 'gün' : 'gün';
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <div className="reservation-page">
            <div className="container">
                <div className="page-header">
                    <h1 className="page-title">{t('reservationForm')}</h1>
                </div>

                <div className="reservation-content">
                    <form onSubmit={handleSubmit} className="reservation-form">
                        {/* Araç Seçimi */}
                        <div className="form-section">
                            <h2 className="section-title">{t('selectVehicle')}</h2>
                            <div className="form-group">
                                <select
                                    name="vehicle_id"
                                    value={formData.vehicle_id}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                >
                                    <option value="">{t('selectVehicle')}</option>
                                    {vehicles.map(vehicle => (
                                        <option key={vehicle.id} value={vehicle.id}>
                                            {getVehicleName(vehicle)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Seçilen Araç Önizlemesi */}
                            {selectedVehicle && (
                                <div className="selected-vehicle-preview">
                                    <div className="preview-image">
                                        {selectedVehicle.images && selectedVehicle.images.length > 0 ? (
                                            <img
                                                src={getImageUrl(selectedVehicle.images[0])}
                                                alt={getVehicleName(selectedVehicle)}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div className="preview-placeholder" style={{ display: selectedVehicle.images?.length > 0 ? 'none' : 'flex' }}>
                                            🚕
                                        </div>
                                    </div>
                                    <div className="preview-info">
                                        <h3>{getVehicleName(selectedVehicle)}</h3>
                                        <p className="preview-price">{t('contactForPrice')}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Kişisel Bilgiler */}
                        <div className="form-section">
                            <h2 className="section-title">{t('personalInfo')}</h2>

                            <div className="form-group">
                                <label>{t('fullName')} *</label>
                                <input
                                    type="text"
                                    name="customer_name"
                                    value={formData.customer_name}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('phone')} *</label>
                                <input
                                    type="tel"
                                    name="customer_phone"
                                    value={formData.customer_phone}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="+90 XXX XXX XX XX"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('email')} *</label>
                                <input
                                    type="email"
                                    name="customer_email"
                                    value={formData.customer_email}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                />
                            </div>
                        </div>

                        {/* Rezervasyon Detayları */}
                        <div className="form-section">
                            <h2 className="section-title">{t('reservationDetails')}</h2>

                            <div className="form-group">
                                <label>{t('pickupLocation')} *</label>
                                <input
                                    type="text"
                                    name="pickup_location"
                                    value={formData.pickup_location}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('dropoffLocation')} *</label>
                                <input
                                    type="text"
                                    name="dropoff_location"
                                    value={formData.dropoff_location}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                />
                            </div>

                            {/* Kiralama Tipi Seçimi */}
                            <div className="form-group">
                                <label>Kiralama Tipi *</label>
                                <div className="rental-type-selector">
                                    <button
                                        type="button"
                                        className={`rental-type-btn ${formData.rental_type === 'hourly' ? 'active' : ''}`}
                                        onClick={() => {
                                            setFormData({
                                                ...formData,
                                                rental_type: 'hourly',
                                                duration_hours: 1,
                                                start_hour: ''
                                            });
                                        }}
                                    >
                                        <span className="type-icon">⏰</span>
                                        <span className="type-title">Saatlik Kiralama</span>
                                        <span className="type-desc">1-12 saat arası</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`rental-type-btn ${formData.rental_type === 'daily' ? 'active' : ''}`}
                                        onClick={() => {
                                            setFormData({
                                                ...formData,
                                                rental_type: 'daily',
                                                duration_hours: 1,
                                                start_hour: ''
                                            });
                                        }}
                                    >
                                        <span className="type-icon">📅</span>
                                        <span className="type-title">Günlük Kiralama</span>
                                        <span className="type-desc">1-10 gün arası</span>
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('date')} *</label>
                                <DatePicker
                                    selected={formData.reservation_date}
                                    onChange={handleDateChange}
                                    minDate={getMinDate()}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                    placeholderText={t('selectDate')}
                                    required
                                />
                            </div>

                            {/* Saatlik Kiralama için Saat Seçimi */}
                            {formData.rental_type === 'hourly' && formData.reservation_date && getAvailableStartHours().length > 0 && (
                                <div className="form-group">
                                    <label>{t('startTime')} *</label>
                                    <select
                                        name="start_hour"
                                        value={formData.start_hour}
                                        onChange={handleChange}
                                        className="form-control"
                                        required
                                    >
                                        <option value="">{t('selectTime')}</option>
                                        {getAvailableStartHours().map(hour => (
                                            <option key={hour} value={hour}>
                                                {String(hour).padStart(2, '0')}:00
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Süre Seçimi */}
                            {((formData.rental_type === 'hourly' && formData.start_hour) || (formData.rental_type === 'daily' && formData.reservation_date)) && (
                                <div className="form-group">
                                    <label>
                                        {getDurationLabel()} *
                                    </label>
                                    <select
                                        name="duration_hours"
                                        value={formData.duration_hours}
                                        onChange={handleChange}
                                        className="form-control"
                                        required
                                    >
                                        {formData.rental_type === 'hourly' ? (
                                            // Saatlik: 1-12 saat
                                            getAvailableDurations().map(hours => (
                                                <option key={hours} value={hours}>
                                                    {hours} {getDurationUnit(hours)}
                                                </option>
                                            ))
                                        ) : (
                                            // Günlük: 1-10 gün
                                            Array.from({ length: 10 }, (_, i) => i + 1).map(days => (
                                                <option key={days} value={days}>
                                                    {days} {getDurationUnit(days)}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Fiyat Bilgisi */}
                        {selectedVehicle && formData.duration_hours && (
                            <div className="price-summary" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '1rem' }}>
                                <div className="price-row" style={{ justifyContent: 'center' }}>
                                    <span className="price-amount" style={{ fontSize: '1.1rem', color: '#1e293b', fontWeight: '500', textAlign: 'center' }}>
                                        ℹ️ {t('contactForPrice')}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="btn btn-primary btn-large btn-block"
                            disabled={submitting}
                        >
                            {submitting ? 'Gönderiliyor...' : t('completeReservation')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Reservation;